import { isNonEmptyString } from '../types';
import { isEasyAzonEmbedData, isLegacyEasyAzonEmbedData } from './types';
const CTA_DEFAULT_BUTTON_TEXT = 'Check Price';
const TX_ADDL_PARAMS = ['author', 'page_type', 'partner', 'section', 'site_id', 'mc'];
const LINK_WRAPPER_FNS = {
    Skimlinks: (linkUrl, params) => {
        if (params.publisherId === undefined || !params.publisherId) {
            console.warn(`Skimlinks: no publisherId set for ${linkUrl}`);
            return linkUrl;
        }
        return `https://go.skimresources.com/?id=${params.publisherId}&url=${safeEncodeUrl(linkUrl)}`;
    },
    Trackonomics: (linkUrl, params) => {
        if (params.siteCode === undefined || !params.siteCode) {
            console.warn(`Trackonomics: no siteCode set for ${linkUrl}`);
            return linkUrl;
        }
        const urlParams = [];
        urlParams.push(`event_type=${params.eventType ?? 'click'}`);
        urlParams.push(`q=${safeEncodeUrl(linkUrl)}`);
        let pageUrl = params.pageUrl ?? '';
        if (pageUrl) {
            pageUrl = encodeURIComponent(decodeURIComponent(pageUrl));
            urlParams.push(`p=${pageUrl}`);
        }
        if (params.item_id !== undefined) {
            urlParams.push(`ContentId=${params.item_id}`);
        }
        urlParams.push(...TX_ADDL_PARAMS.filter((k) => !!params[k]).map((k) => `${k}=${encodeURIComponent(params[k])}`));
        return `https://clicks.trx-hub.com/xid/${params.siteCode}?${urlParams.join('&')}`;
    },
};
/**
 * Encode a URL for use as a query string parameter in a wrapped URL, accounting for
 * two factors:
 * 1) Because users can paste in an arbitrary URL obtained through various workflows, it
 * may already have URL-encoded characters that we don't want to double-encode. Therefore,
 * we decode before encoding.
 * 2) However, the URL we're wrapping may itself be an affiliate-wrapped URL with the
 * original merchant page URL specified in a query string. If that original URL contained
 * query string parameters that are now URL-encoded, we need to preserve that encoding.
 * To handle this, we explicitly double-encode any % characters within the query string,
 * so they will survive the subsequent decode -> encode.
 */
export function safeEncodeUrl(origUrl) {
    try {
        const parsed = new URL(origUrl);
        if (parsed.search?.includes('%')) {
            parsed.search = parsed.search.replace(/%/g, '%25');
            origUrl = parsed.toString();
        }
    }
    catch {
        console.error(`Could not parse URL ${origUrl}`);
        return origUrl;
    }
    let encodedUrl = origUrl;
    try {
        encodedUrl = encodeURIComponent(decodeURIComponent(origUrl));
    }
    catch {
        console.error(`Could not decode URL ${origUrl}`);
    }
    return encodedUrl;
}
export function applyLinkWrapper(linkUrl, wrapperConfig) {
    if (LINK_WRAPPER_FNS[wrapperConfig.vendor] === undefined) {
        console.warn(`unknown link wrapper vendor ${wrapperConfig.vendor}`);
        return linkUrl;
    }
    return LINK_WRAPPER_FNS[wrapperConfig.vendor](linkUrl, wrapperConfig.params);
}
/**
 * Extract link wrappers from the "options" query parameter of the mvn:// URL,
 * which contains serialized JSON.
 *
 * Multiple wrappers are supported, applied successively in the order in which
 * they appear in the options.
 *
 * We expect the following shape:
 * {
 *   "linkWrappers": [
 *     {
 *       "vendor": "...",
 *       "params": {
 *         ...
 *       }
 *     }
 *   ]
 * }
 * ...where "vendor" is a key supported in `linkWrapperFns`, and "params" contains
 * vendor-specific keys and values that will be used in constructing the wrapped URL.
 */
export function getLinkWrappersFromConfigUrl(configUrl) {
    const optionStr = configUrl.searchParams.get('options') ?? '';
    let options;
    if (optionStr !== '') {
        try {
            // TODO: runtime type validation
            options = JSON.parse(optionStr);
        }
        catch {
            // Invalid JSON
            console.error(`getLinkWrappersFromConfigUrl:: Invalid JSON in configUrl "${optionStr}"`);
        }
    }
    return options?.linkWrappers ?? [];
}
export const buildCommerceCtaConfig = (configStr, disableLinkWrappers) => {
    let configUrl;
    try {
        configUrl = new URL(configStr);
    }
    catch {
        // invalid url value
        return;
    }
    let link = configUrl.searchParams.get('link') ?? '';
    if (Boolean(disableLinkWrappers) === false) {
        link = getLinkWrappersFromConfigUrl(configUrl).reduce(applyLinkWrapper, link);
    }
    let identifiers = {};
    try {
        identifiers = JSON.parse(configUrl.searchParams.get('identifiers') ?? '{}');
    }
    catch (e) {
        console.log(`
            Error when parsing identifiers from ${configUrl.toString()}: ${e instanceof Error ? e?.message : 'unknown'}`);
    }
    return {
        link,
        ctaText: (configUrl.searchParams.get('ctaText') ?? '') || CTA_DEFAULT_BUTTON_TEXT,
        identifiers,
    };
};
function getEasyAzonFormattedOffers(parsed) {
    return isEasyAzonEmbedData(parsed) ? parsed.formatted?.offers : undefined;
}
function buildEasyAzonOfferUrl(parsed) {
    if (isNonEmptyString(parsed.retrievedData?.url)) {
        return parsed.retrievedData.url;
    }
    const tag = isEasyAzonEmbedData(parsed) ? parsed.retrievedData.affiliateTag : parsed.tag;
    const identifier = isEasyAzonEmbedData(parsed) ? parsed.retrievedData.identifier : parsed.identifier;
    return `https://www.amazon.com/dp/${identifier}/?tag=${tag}&linkCode=ogi&th=1&psc=1`;
}
function buildEasyAzonDefaultOffer(parsed) {
    if (parsed.retrievedData.offer === undefined)
        return undefined;
    // The paradeInitialPulldate is a datetime int val representing the
    // pull date of the static data used for parade replatforming.  Once the
    // commerce api is in place, this shim should be removed and a log/notice
    // raised when a product without a pull date is encountered.
    const PARADE_INITIAL_PULLDATE = 1656601200000;
    let { pullDate } = parsed.retrievedData.offer;
    // !pullDate or the original parsed.retrievedData.offer.pullDate || PARADE_INITIAL_PULLDATE
    // would work fine, but the maven-eslint-strict rules don't like it.
    if (pullDate <= 0) {
        pullDate = PARADE_INITIAL_PULLDATE;
    }
    return {
        // no value was returned in the Phoenix version.
        identifier: '',
        locale: isEasyAzonEmbedData(parsed) ? parsed.retrievedData.locale : parsed.locale,
        price: parsed.retrievedData.offer.price,
        pullDate,
        saved: parsed.retrievedData.offer.saved,
        url: buildEasyAzonOfferUrl(parsed),
        vendor: 'Amazon', // Default Easy Azon is always amazon
    };
}
function buildEasyAzonOffers(parsed) {
    const defaultOffer = buildEasyAzonDefaultOffer(parsed);
    const offer = defaultOffer ? [defaultOffer] : getEasyAzonFormattedOffers(parsed);
    return offer ?? [];
}
function extractAmazonProductImageKey(image) {
    if (typeof image !== 'object' || image === null || typeof image.url !== 'string') {
        return undefined;
    }
    const fileChunk = image.url.split('/').pop();
    if (typeof fileChunk !== 'string') {
        return undefined;
    }
    const keyEndPos = fileChunk.indexOf('._SL');
    return keyEndPos === -1 ? undefined : fileChunk.slice(0, keyEndPos);
}
function buildEasyAzonImages(parsed) {
    if (!parsed.retrievedData.images) {
        return [];
    }
    const images = {};
    // sort ascending by width
    const sortedImages = parsed.retrievedData.images.slice().sort((a, b) => a.width - b.width);
    sortedImages.forEach((image) => {
        const imageKey = extractAmazonProductImageKey(image) ?? 'unknown';
        if (images[imageKey] === undefined) {
            images[imageKey] = { isWebP: false, sizes: [] };
        }
        if (!images[imageKey].sizes.some((size) => size.src === image.url)) {
            images[imageKey].sizes.push({
                src: image.url,
                height: image.height,
                width: image.width,
            });
        }
    });
    return Object.values(images);
}
function buildCommerceImages(parsed) {
    const easyAzonImages = buildEasyAzonImages(parsed);
    if (easyAzonImages.length !== 0) {
        return easyAzonImages;
    }
    if (isEasyAzonEmbedData(parsed) && parsed.formatted?.images) {
        return parsed.formatted.images;
    }
    return [];
}
function buildEasyAzonIdentifiers(parsed) {
    let identifiers;
    if (isEasyAzonEmbedData(parsed)) {
        identifiers = parsed.formatted?.identifiers ?? {};
    }
    else {
        identifiers = parsed.identifiers ?? {};
        // handling legacy easyazon format
        if (isLegacyEasyAzonEmbedData(parsed) && parsed.identifier) {
            identifiers['ASIN'] = parsed.identifier;
        }
    }
    return identifiers;
}
function buildEasyAzonItem(parsed) {
    if (isEasyAzonEmbedData(parsed) && parsed.formatted?.offers) {
        return parsed.formatted;
    }
    return {
        title: (parsed.retrievedData.title ?? '') || 'Unknown Title',
        identifiers: buildEasyAzonIdentifiers(parsed),
        offers: buildEasyAzonOffers(parsed),
        images: buildCommerceImages(parsed),
    };
}
export function buildEasyAzonInfoblockConfig(configUrl, disableLinkWrappers) {
    if (typeof configUrl === 'string') {
        try {
            configUrl = new URL(configUrl);
        }
        catch {
            // invalid url string
            return null;
        }
    }
    const shortcodeData = configUrl.searchParams?.get('data') ?? '';
    if (shortcodeData === '') {
        // unable to pull enough data from the definition, bailing
        return null;
    }
    let parsed;
    try {
        // TODO: runtime data validation
        parsed = JSON.parse(shortcodeData);
    }
    catch (_e) {
        // unable to pull enough data from the definition, bailing
        return null;
    }
    if (parsed.retrievedData?.offer == null) {
        return null;
    }
    const item = buildEasyAzonItem(parsed);
    if (Boolean(disableLinkWrappers) === false) {
        getLinkWrappersFromConfigUrl(configUrl).forEach((wrapper) => {
            item.offers?.forEach((offer) => {
                offer.url = applyLinkWrapper(offer.url, wrapper);
            });
        });
    }
    return {
        item,
        title: (parsed.retrievedData?.title ?? '') || undefined,
        // These fields don't exist in the encoded embed data. Existing page examples
        // have them undefined. Defaults in the commerce-api Skimlinks branch are
        // false. Kept the commented code here just for reference, but it doesnt
        // typecheck (a problem phoenix obviously didn't have to worry about).
        // showPrice: Boolean(parsed.showPrice),
        // showAllOffers: Boolean(parsed.showAllOffers) || undefined,
    };
}
//# sourceMappingURL=commerceProduct.js.map